// Layout calculation helpers for flow diagram
import { breakTextToLines } from './textHelpers'

// Constants for layout calculations
const HEADER_HEIGHT = 80
const PILL_HEIGHT = 19
const GAP_ABOVE_TEXT = 10
const LINE_HEIGHT = 14
const FONT_SIZE = 12
const MIN_ROW_GAP = 60
const BOTTOM_PADDING = 40
const TOP_PADDING = 70

// Calculate actual text height based on line count
// Accounts for line spacing (dy) and actual font height of the last line
export const calculateTextHeight = (textLines, secondaryLines) => {
  const totalLines = textLines.length + secondaryLines.length
  if (totalLines === 0) return 0
  // Line spacing for all lines + extra height for the last line's actual font size
  return (totalLines - 1) * LINE_HEIGHT + FONT_SIZE
}

// Calculate height needed for a single row
export const calculateRowHeight = (textLines, secondaryLines) => {
  const textHeight = calculateTextHeight(textLines, secondaryLines)
  return PILL_HEIGHT + GAP_ABOVE_TEXT + textHeight
}

// Calculate dynamic Y positions for export mode (no scrolling, all content visible)
export const calculateDynamicYPositions = (themes, formatCurrency) => {
  const rowHeights = []
  const yPositions = []
  let currentY = TOP_PADDING

  themes.forEach((theme, i) => {
    // Calculate actual content for each row
    const themeTextLines = breakTextToLines(theme.theme_name || '', 25)
    const outcomeTextLines = breakTextToLines(
      (theme.social_criteria || '').replace(/(^.|\s.)/g, c => c.toUpperCase()),
      25
    )

    // Impact column has both primary and secondary text
    const impactText =
      typeof formatCurrency === 'function' ? formatCurrency(theme.total_impact) : String(theme.total_impact)
    const impactTextLines = breakTextToLines(impactText, 25)
    const stakeholderText = `${theme.stakeholder_count || 0} stakeholders`
    const stakeholderLines = breakTextToLines(stakeholderText, 25)

    // Find the maximum height among all three columns
    const themeHeight = calculateRowHeight(themeTextLines, [])
    const outcomeHeight = calculateRowHeight(outcomeTextLines, [])
    const impactHeight = calculateRowHeight(impactTextLines, stakeholderLines)

    const maxHeight = Math.max(themeHeight, outcomeHeight, impactHeight)

    rowHeights.push(maxHeight)
    yPositions.push(currentY)
    currentY += maxHeight + MIN_ROW_GAP
  })

  const totalHeight = currentY - MIN_ROW_GAP + BOTTOM_PADDING

  return {
    yPositions,
    rowHeights,
    totalHeight
  }
}

export const calculateLayout = (themes, baseWidth = 1000, isExporting = false, formatCurrency = null) => {
  // Set pill dimensions
  const pillW = 187
  const pillH = 19
  const leftPillW = pillW // same width for all pills

  // Calculate positions to match design layout
  const leftMargin = 60 // Distance from left edge to SV Theme pill
  const svThemeToFirstColumnGap = 120 // Distance between SV Theme pill and first theme column
  const columnGap = (baseWidth - leftMargin - leftPillW - svThemeToFirstColumnGap - pillW * 3) / 2 // Distance between social criteria and impact columns

  // Columns X - positioned to match design spacing
  const xThemeBase = leftMargin
  const xTheme = leftMargin + leftPillW + svThemeToFirstColumnGap
  const xOutcome = xTheme + pillW + columnGap
  const xImpact = xOutcome + pillW + columnGap

  let baseHeight, yPositions, rowHeights

  if (isExporting && formatCurrency) {
    // Dynamic layout for export - calculate actual content heights
    const dynamicLayout = calculateDynamicYPositions(themes, formatCurrency)
    baseHeight = dynamicLayout.totalHeight
    yPositions = dynamicLayout.yPositions
    rowHeights = dynamicLayout.rowHeights
  } else {
    // Fixed layout for normal display
    const pillHeight = 19
    const textBelowPillSpace = 10 + 17 // 10px gap + 17px text height (typical)
    const totalPillSpace = pillHeight + textBelowPillSpace
    const rowGap = 60 // Reduced gap between rows

    // Calculate height with extra space at the end for foreignObject (60px)
    // instead of typical text height (17px)
    const extraHeightForScrollable = 60 - 17 // Additional 43px for scrollable content
    const contentHeight =
      HEADER_HEIGHT +
      themes.length * totalPillSpace +
      (themes.length - 1) * rowGap +
      extraHeightForScrollable +
      BOTTOM_PADDING
    baseHeight = Math.max(300, contentHeight) // Minimum reasonable height

    const verticalSpaceBetweenRows = totalPillSpace + rowGap

    // Generate fixed Y positions
    yPositions = themes.map((_, i) => TOP_PADDING + i * verticalSpaceBetweenRows)
    rowHeights = themes.map(() => totalPillSpace)
  }

  return {
    baseWidth,
    baseHeight,
    topPad: TOP_PADDING,
    rowGap: MIN_ROW_GAP,
    pillW,
    pillH,
    leftPillW,
    columnGap,
    svThemeToFirstColumnGap,
    xThemeBase,
    xTheme,
    xOutcome,
    xImpact,
    yPositions, // Dynamic or fixed Y positions
    rowHeights, // Heights for each row
    isExporting // Pass through export mode flag
  }
}
